import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Users, Building2, Gavel, HandHeart, Shield, Target, CheckCircle, Hammer, Briefcase, FileText, UserCheck, TrendingDown, BarChart3 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';

// Employment Impact Data - Based on Bureau of Justice Statistics and Urban Institute research
const employmentImpactData = [
  { category: 'Unemployed at Release', recidivismRate: 68, crimeRate: 45, color: '#ef4444' },
  { category: 'Employed within 30 Days', recidivismRate: 32, crimeRate: 18, color: '#f97316' },
  { category: 'Employed within 90 Days', recidivismRate: 23, crimeRate: 12, color: '#eab308' },
  { category: 'Stable Employment (6+ months)', recidivismRate: 13, crimeRate: 7, color: '#22c55e' }
];

const timelineData = [
  { month: 'Release', unemployed: 68, employed: 13 },
  { month: '3 Months', unemployed: 58, employed: 18 },
  { month: '6 Months', unemployed: 45, employed: 23 },
  { month: '12 Months', unemployed: 42, employed: 13 },
  { month: '24 Months', unemployed: 38, employed: 11 }
];

const costBenefitData = [
  { name: 'Program Cost per Person', value: 3500, fill: '#3b82f6' },
  { name: 'Incarceration Cost Avoided', value: 31000, fill: '#22c55e' },
  { name: 'Net Savings per Success', value: 27500, fill: '#10b981' }
];

export default function P2PPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            P2P Assignment
          </h1>
          <p className="text-xl text-slate-600 mb-8 max-w-3xl mx-auto">
            Connecting policy, practice, and people to create meaningful employment opportunities for justice-involved individuals
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Badge variant="secondary" className="px-4 py-2">Policy Innovation</Badge>
            <Badge variant="secondary" className="px-4 py-2">Workforce Development</Badge>
            <Badge variant="secondary" className="px-4 py-2">Community Partnership</Badge>
          </div>
        </div>
      </section>

      {/* VPSA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Hammer className="w-8 h-8 text-blue-600" />
              <h2 className="text-3xl font-bold text-slate-900">VPSA</h2>
            </div>
            <h3 className="text-2xl font-semibold text-slate-800 mb-4">
              Employment Support for People in Reentry (RRC-Focused)
            </h3>
            <h4 className="text-xl text-blue-600 font-medium">
              The First 90 Days Matter — Let's Make Them Count
            </h4>
          </div>

          {/* Audience */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                Audience
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline">Employers</Badge>
                <Badge variant="outline">Halfway House Staff</Badge>
                <Badge variant="outline">Workforce Partners</Badge>
                <Badge variant="outline">Community Members</Badge>
              </div>
            </CardContent>
          </Card>

          {/* Values Section */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HandHeart className="w-5 h-5 text-green-600" />
                Why We Do This — and Why You Should Too
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-slate-700 leading-relaxed">
                We believe in second chances, in the dignity of work, and in the idea that when people do their time, 
                they should be allowed to come home and rebuild. Employment isn't just about income — it's about 
                restoring someone's place in their family, their community, and their future.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <Shield className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-slate-800 mb-1">Safety</h4>
                    <p className="text-slate-600">Stable people are less likely to reoffend.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <UserCheck className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-slate-800 mb-1">Responsibility</h4>
                    <p className="text-slate-600">People want to work and provide for themselves.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <HandHeart className="w-6 h-6 text-purple-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-slate-800 mb-1">Redemption</h4>
                    <p className="text-slate-600">No one should be defined by the worst thing they've ever done.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Gavel className="w-6 h-6 text-orange-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-slate-800 mb-1">Fairness</h4>
                    <p className="text-slate-600">If we say we believe in justice, we should also believe in what comes after justice — opportunity.</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Data Visualization Section */}
          <section className="mb-8">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-slate-900 mb-4 flex items-center justify-center gap-3">
                <BarChart3 className="w-7 h-7 text-blue-600" />
                The Employment Impact: What the Data Shows
              </h3>
              <p className="text-slate-600 max-w-3xl mx-auto">
                Research from the Bureau of Justice Statistics and Urban Institute demonstrates the powerful connection between employment and crime reduction.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* Employment vs Recidivism Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingDown className="w-5 h-5 text-green-600" />
                    Employment Timing vs. Recidivism Rate
                  </CardTitle>
                  <CardDescription>
                    How quickly someone finds employment dramatically affects their likelihood of reoffending
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={employmentImpactData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="category" 
                        angle={-45}
                        textAnchor="end"
                        height={80}
                        fontSize={12}
                      />
                      <YAxis label={{ value: 'Recidivism Rate (%)', angle: -90, position: 'insideLeft' }} />
                      <Tooltip 
                        formatter={(value, name) => [`${value}%`, 'Recidivism Rate']}
                        labelStyle={{ color: '#1e293b' }}
                      />
                      <Bar 
                        dataKey="recidivismRate" 
                        fill="#3b82f6"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="mt-4 text-sm text-slate-600">
                    <p><strong>Key Insight:</strong> Stable employment reduces recidivism by 81% compared to unemployment at release.</p>
                  </div>
                </CardContent>
              </Card>

              {/* Crime Rate Impact */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-blue-600" />
                    Employment vs. Crime Rate
                  </CardTitle>
                  <CardDescription>
                    How employment status correlates with new criminal activity
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={employmentImpactData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="category" 
                        angle={-45}
                        textAnchor="end"
                        height={80}
                        fontSize={12}
                      />
                      <YAxis label={{ value: 'New Crime Rate (%)', angle: -90, position: 'insideLeft' }} />
                      <Tooltip 
                        formatter={(value, name) => [`${value}%`, 'New Crime Rate']}
                        labelStyle={{ color: '#1e293b' }}
                      />
                      <Bar 
                        dataKey="crimeRate" 
                        fill="#ef4444"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="mt-4 text-sm text-slate-600">
                    <p><strong>Key Insight:</strong> Stable employment reduces new criminal activity by 84% compared to unemployment.</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Timeline Comparison */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-purple-600" />
                  Recidivism Over Time: Employed vs. Unemployed
                </CardTitle>
                <CardDescription>
                  Two-year tracking of reoffense rates based on employment status at release
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={timelineData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis label={{ value: 'Recidivism Rate (%)', angle: -90, position: 'insideLeft' }} />
                    <Tooltip 
                      formatter={(value, name) => [`${value}%`, name === 'unemployed' ? 'Unemployed at Release' : 'Employed at Release']}
                      labelStyle={{ color: '#1e293b' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="unemployed" 
                      stroke="#ef4444" 
                      strokeWidth={3}
                      name="Unemployed at Release"
                      dot={{ fill: '#ef4444', strokeWidth: 2, r: 5 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="employed" 
                      stroke="#22c55e" 
                      strokeWidth={3}
                      name="Employed at Release"
                      dot={{ fill: '#22c55e', strokeWidth: 2, r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-red-500 rounded"></div>
                    <span className="text-slate-600"><strong>Unemployed at Release:</strong> Recidivism remains high throughout the tracking period</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-green-500 rounded"></div>
                    <span className="text-slate-600"><strong>Employed at Release:</strong> Consistently lower reoffense rates over time</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Cost-Benefit Analysis */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-green-600" />
                  Economic Impact: Cost vs. Savings
                </CardTitle>
                <CardDescription>
                  Investment in employment programs compared to incarceration costs avoided
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600 mb-2">$3,500</div>
                    <div className="text-sm text-slate-600">Average Cost per Person</div>
                    <div className="text-xs text-slate-500 mt-1">Employment program investment</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600 mb-2">$31,000</div>
                    <div className="text-sm text-slate-600">Incarceration Cost Avoided</div>
                    <div className="text-xs text-slate-500 mt-1">Per successful participant</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-emerald-600 mb-2">$27,500</div>
                    <div className="text-sm text-slate-600">Net Savings</div>
                    <div className="text-xs text-slate-500 mt-1">Return on investment</div>
                  </div>
                </div>
                <div className="mt-6 p-4 bg-green-50 rounded-lg">
                  <p className="text-green-800 font-medium text-center">
                    <strong>785% Return on Investment:</strong> Every dollar spent on employment programs saves nearly $8 in incarceration costs
                  </p>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Problem Section */}
          <Card className="mb-8 border-red-200 bg-red-50">
            <CardHeader>
              <CardTitle className="text-red-800">
                Problem: The System Makes Reentry Harder Than It Has to Be
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-red-700 font-medium">
                Most people in RRCs want to work — but the system isn't built to support them.
              </p>
              
              <ul className="space-y-3 text-red-700">
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></span>
                  They often leave prison without IDs, Social Security cards, or bank access.
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></span>
                  Many don't know how to explain their record to an employer.
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></span>
                  Employers often say they support second chances, but won't follow through.
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></span>
                  Without a job lined up before full release, people return home under stress, with no income and limited support.
                </li>
              </ul>
              
              <div className="mt-4 p-4 bg-red-100 rounded-lg">
                <p className="text-red-800 font-medium">
                  This creates a revolving door. And that hurts all of us — from taxpayers to employers to families.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Solution Section */}
          <Card className="mb-8 border-green-200 bg-green-50">
            <CardHeader>
              <CardTitle className="text-green-800">
                Solution: Start with Employment — Start Inside the RRC
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-green-700 font-medium mb-6">
                We can change this by acting early — while people are still in the halfway house:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                  <span className="text-green-700">Help them secure documents (ID, SSN, birth cert)</span>
                </div>
                
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                  <span className="text-green-700">Provide job-readiness coaching and interview prep</span>
                </div>
                
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                  <span className="text-green-700">Teach them how to confidently explain their record</span>
                </div>
                
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                  <span className="text-green-700">Partner with second-chance employers and local trades</span>
                </div>
                
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                  <span className="text-green-700">Explain employer tax benefits like the Work Opportunity Tax Credit</span>
                </div>
                
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                  <span className="text-green-700">Hold hiring fairs before they exit, so they walk out employed</span>
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-green-100 rounded-lg">
                <p className="text-green-800 font-medium">
                  When people reenter with purpose, it changes everything — for them, their families, and their neighborhoods.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Action Section */}
          <Card className="border-blue-200 bg-blue-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-800">
                <Target className="w-5 h-5" />
                Action: What You Can Do
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <Hammer className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-blue-800 mb-1">If you're a workforce leader:</h4>
                    <p className="text-blue-700">Bring job coaches or job developers into your local RRC.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Building2 className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-blue-800 mb-1">If you're an employer:</h4>
                    <p className="text-blue-700">Partner with reentry programs and say yes to second-chance candidates.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Gavel className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-blue-800 mb-1">If you're a policymaker:</h4>
                    <p className="text-blue-700">Fund services that start before full release, not after.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <HandHeart className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-blue-800 mb-1">If you're a community advocate:</h4>
                    <p className="text-blue-700">Make this your issue — because when people succeed after prison, everyone benefits.</p>
                  </div>
                </div>
              </div>
              
              <Separator />
              
              <div className="text-center">
                <p className="text-blue-800 font-medium text-lg mb-4">
                  We all want safer communities, stronger families, and fewer people behind bars.
                </p>
                <p className="text-blue-700 text-lg">
                  Employment makes that possible. Let's stop waiting. Let's start reentry before the door opens.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
}